import { Platform } from 'react-native';
import Sound from 'react-native-sound';
import { getActiveResolvedVisualThemeId } from '../theme/activeVisualThemeRef';
import { isMediaPlaybackActive } from './mediaPlaybackFocus';
import { SOUND_VOLUME, areSoundsEnabled, applyAudioCategory, claimMessageSoundSlot } from './appSounds';
import {
  messageSoundAndroidRes,
  messageSoundIosFilename,
  messageSoundWebAsset,
} from '../theme/themeSounds';

/** Хитрый «прайм» HTMLAudio: один тихий play+pause на жесте, чтобы потом autoplay не блокировался. */
let webHtmlAudioPrimed = false;

/** Вызовите из обработчика нажатия (логин и т.д.), иначе Safari может глушить звук сообщений. */
export function tryUnlockWebAudio(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const W = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const AC = window.AudioContext ?? W.webkitAudioContext;
    if (AC) {
      const ctx = new AC();
      void ctx.resume();
      ctx.close();
    }
  } catch {
    /* noop */
  }
  if (webHtmlAudioPrimed) return;
  try {
    const HtmlAudio = globalThis.Audio;
    if (!HtmlAudio) return;
    const uri = messageSoundWebAsset(getActiveResolvedVisualThemeId()) || '';
    if (!uri) return;
    /** Прогоняем play→pause на жесте, чтобы дальнейшие проигрывания не натыкались на autoplay-policy. */
    const a = new HtmlAudio(uri);
    a.volume = 0;
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === 'function') {
      void p
        .then(() => {
          try {
            a.pause();
            a.currentTime = 0;
          } catch {
            /* noop */
          }
          webHtmlAudioPrimed = true;
        })
        .catch(() => {});
    } else {
      try {
        a.pause();
      } catch {
        /* noop */
      }
      webHtmlAudioPrimed = true;
    }
  } catch {
    /* noop */
  }
}

/** Звук входящего сообщения (веб и натив), по текущему визуальному стилю. */
export async function playIncomingMessageSound(): Promise<void> {
  /**
   * Пока пользователь смотрит видео или слушает голосовое — молчим. Этот звук
   * идёт с категорией Playback и забирает аудиофокус, из-за чего плеер видео
   * встаёт на паузу (стори 5.4). Уведомление о сообщении всё равно приходит
   * визуально, а прерванный просмотр раздражает сильнее пропущенного «дзынь».
   */
  if (isMediaPlaybackActive()) {
    return;
  }
  /** Переключатель «Звуки» из настроек — раньше выключить их было нельзя вообще. */
  if (!areSoundsEnabled()) {
    return;
  }
  /**
   * Не чаще одного раза в две секунды. Звук играет на каждое входящее, и в живой группе это
   * давало очередь накладывающихся «дзынь»: десять сообщений — десять одновременных
   * проигрывателей. Слот занимается тем же вызовом, что и проверяется, иначе два сообщения
   * из одного пакета WS проскочат между проверкой и отметкой.
   */
  if (!claimMessageSoundSlot()) {
    return;
  }
  const theme = getActiveResolvedVisualThemeId();
  try {
    if (Platform.OS === 'web') {
      const uri = messageSoundWebAsset(theme) || '';
      if (!uri) return;
      const HtmlAudio = globalThis.Audio;
      const audio = new HtmlAudio(uri);
      audio.volume = SOUND_VOLUME.message;
      audio.preload = 'auto';
      try {
        audio.load();
      } catch {
        /* noop */
      }
      const p = audio.play();
      void (p && typeof p.catch === 'function' ? p.catch(() => {}) : null);
      return;
    }

    /**
     * `Ambient`, а не `Playback`, — и это то самое место, из-за которого приложение звенело
     * при выключенном звуке телефона. На iOS `Playback` играет поверх бокового переключателя
     * «звонок / бесшумно», на Android уходит в `STREAM_MUSIC`, которого режимы «без звука» и
     * «вибрация» не касаются. `Ambient` — уведомительный поток на обеих платформах, его ОС
     * глушит сама. Категория читается при `new Sound(...)`, поэтому выставляется до него.
     */
    applyAudioCategory('message');
    const androidRes = messageSoundAndroidRes(theme);
    const iosFile = messageSoundIosFilename(theme);
    await new Promise<void>((resolve, reject) => {
      const handleReady = (s: Sound, err: unknown) => {
        if (err) {
          reject(err);
          return;
        }
        // Раньше громкость не выставлялась вовсе — то есть 1.0. Вместе с варяжским
        // «axe», который был на 9 дБ громче остальных тем, это и давало «мега громко».
        s.setVolume(SOUND_VOLUME.message);
        s.play(() => {
          s.release();
          resolve();
        });
      };
      if (Platform.OS === 'android') {
        const s = new Sound(androidRes, '', (err) => handleReady(s, err));
      } else {
        const s = new Sound(iosFile, Sound.MAIN_BUNDLE, (err) => handleReady(s, err));
      }
    });
  } catch {
    /* нет динамика */
  }
}
